/**
 * SaveDestinations — the report destinations (T-12/T-13): daily,
 * weekly, internal-only, and the client report with its second confirm
 * (AC-1g-17). The client checkbox stays inert while the client report is gated.
 */

import type { DestChoice } from "../triage/serialize-dest";
import { CLIENT_REPORT_COMING_SOON, CLIENT_REPORT_ENABLED } from "../triage/client-report-gate";

export interface SaveDestinationsProps {
  destChoice: DestChoice;
  clientPending: boolean;
  clientConfirmed: boolean;
  handleDailyToggle: () => void;
  handleWeeklyToggle: () => void;
  handleInternalOnlyToggle: () => void;
  handleClientFirstTap: () => void;
  handleClientConfirmYes: () => void;
  handleClientConfirmCancel: () => void;
  onComingSoon?: (message: string) => void;
}

export function SaveDestinations({
  destChoice,
  clientPending,
  clientConfirmed,
  handleDailyToggle,
  handleWeeklyToggle,
  handleInternalOnlyToggle,
  handleClientFirstTap,
  handleClientConfirmYes,
  handleClientConfirmCancel,
  onComingSoon,
}: SaveDestinationsProps) {
  return (
    <>
      <section data-testid="save-screen-dest" class="save-chip-group save-dest-group">
        <p class="save-chip-group-hint">Report destination</p>
        <label>
          <input
            type="checkbox"
            data-testid="save-screen-dest-daily"
            checked={destChoice.kind === "reports" && destChoice.daily}
            disabled={destChoice.kind === "internal_only"}
            onChange={handleDailyToggle}
          />
          Daily report
        </label>
        <label>
          <input
            type="checkbox"
            data-testid="save-screen-dest-weekly"
            checked={destChoice.kind === "reports" && destChoice.weekly}
            disabled={destChoice.kind === "internal_only"}
            onChange={handleWeeklyToggle}
          />
          Weekly report
        </label>
        <label>
          <input
            type="checkbox"
            data-testid="save-screen-dest-internal-only"
            checked={destChoice.kind === "internal_only"}
            onChange={handleInternalOnlyToggle}
          />
          Internal only
        </label>
        {/* GATED (client-report-gate.ts): the control is inert until the report
            resolver and the goalkeeper blocklist exist. `disabled` on the input
            makes it un-toggleable and greys it; the tap handler lives on the
            LABEL, which still fires, so this explains itself instead of being a
            dead tap. */}
        <label
          onClick={
            CLIENT_REPORT_ENABLED ? undefined : () => onComingSoon?.(CLIENT_REPORT_COMING_SOON)
          }
        >
          <input
            type="checkbox"
            data-testid="save-screen-dest-client"
            checked={clientConfirmed}
            disabled={destChoice.kind === "internal_only" || !CLIENT_REPORT_ENABLED}
            onChange={handleClientFirstTap}
          />
          Client report
        </label>
        {clientPending && (
          <div data-testid="save-screen-client-confirm">
            <p>Send to the CLIENT’s report?</p>
            <button type="button" data-testid="save-screen-client-confirm-yes" onClick={handleClientConfirmYes}>
              Yes
            </button>
            <button type="button" data-testid="save-screen-client-confirm-cancel" onClick={handleClientConfirmCancel}>
              Cancel
            </button>
          </div>
        )}
      </section>
    </>
  );
}
